import { operationRequirement, type OperationContext } from '@/session/operation';
import type { RequestScope } from '@/session/scopedTransport';
import { utils } from '@/utils';
import { apiCall } from '@/utils/apiCallInstance';
import { isRecord } from '@/utils/dataUtils';
import { fileDownloadFromResponse } from '@/utils/fileDownloadFromResponse';

export type AssetDownloadResult = Readonly<{ kind: 'saved' }> | Readonly<{ kind: 'canceled' }> | Readonly<{ kind: 'failed'; detail?: string }>;

const errorDetail = async (response: Response): Promise<string | undefined> => {
  if (!response.headers.get('content-type')?.includes('application/json')) return undefined;
  const data: unknown = await response.json().catch(() => null);
  return isRecord(data) && isRecord(data.errors) && typeof data.errors.error === 'string' ? data.errors.error : undefined;
};

/**
 * Saves the stored file under its stored name. It is saved only while the click's organization and account are
 * current, so a download can finish after the user moves elsewhere in the same organization.
 */
export const downloadAsset = async (
  scope: RequestScope,
  { id, filename }: Readonly<{ id: number; filename: string }>,
  operation: OperationContext
): Promise<AssetDownloadResult> => {
  if (typeof window === 'undefined' || !operation.isScopeCurrent()) return { kind: 'canceled' };
  try {
    const response = await apiCall().get(`/assets/${id}/download`, undefined, undefined, {
      organizationScope: scope.kind === 'organization' ? { kind: 'organization', organization: scope.organization } : { kind: 'none' },
      operationRequirement: operationRequirement(operation, 'scope'),
    });
    if (!response.ok) return operation.isScopeCurrent() ? { kind: 'failed', detail: await errorDetail(response) } : { kind: 'canceled' };
    const result = await fileDownloadFromResponse(response, filename, { canPublish: operation.isScopeCurrent });
    if (result.success) return { kind: 'saved' };
    return result.canceled ? { kind: 'canceled' } : { kind: 'failed', detail: result.error };
  } catch (error) {
    const failure = utils.error.handleAxiosError(error);
    return utils.error.isCanceledResponse(failure) || !operation.isScopeCurrent()
      ? { kind: 'canceled' }
      : { kind: 'failed', detail: failure.errors.error };
  }
};
